import { useEffect, useState } from 'react';

import { UsePulsingBorderType } from './interfaces';

/**
 * Drives the pulse/pause cycle for PulsingBorder using a recursive timeout chain.
 *
 * @param pulseDurationMs - How long, in milliseconds, the border stays in the pulsing state.
 * @param pauseDurationMs - How long, in milliseconds, the border stays still between pulses.
 * @param active - Whether the pulse/pause cycle should run at all.
 * @returns `isPulsing`, true while the border should be animating.
 */
export const usePulsingBorder: UsePulsingBorderType = (
  pulseDurationMs,
  pauseDurationMs,
  active,
) => {
  const [isPulsing, setIsPulsing] = useState(active);

  useEffect(() => {
    if (!active) {
      setIsPulsing(false);
      return undefined;
    }

    setIsPulsing(true);
    let timeoutId: ReturnType<typeof setTimeout>;

    const schedule = (durationMs: number, nextIsPulsing: boolean) => {
      timeoutId = setTimeout(() => {
        setIsPulsing(nextIsPulsing);
        schedule(
          nextIsPulsing ? pulseDurationMs : pauseDurationMs,
          !nextIsPulsing,
        );
      }, durationMs);
    };

    schedule(pulseDurationMs, false);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [active, pulseDurationMs, pauseDurationMs]);

  return { isPulsing };
};
